import fontsCss from '@static/fonts/fonts.css?raw';
import baseCss from '@static/styles/base.css?raw';
import resetCss from '@static/styles/reset.css?raw';
import variablesCss from '@static/styles/variables.css?raw';
import { themes } from '@toggle/design-system/src/design-tokens/themes/themes';
import { formatTimestampWithTz } from '@toggle/helpers';
import { t } from 'i18next';

import { config } from '~/config';
import { useFlags } from '~/stores/use-flags/useFlags';

import type { ConversationInventory } from '../services/inventory-service';
import { ConversationTask } from '../use-chat/use-chat.types';
import { VisualizationTable } from '../use-visualization-store/useVisualizationStore';
import { buildInventoryContent } from './buildInventoryContent';
import { prepareChartFonts } from './copyAlfredCharts';
import { extractStylesheetRules } from './fetchStylesUtils';
import { getDisclaimer } from './getDisclaimer';
import { BODY_STYLE, htmlNodes } from './htmlNodes';
import { markdownParser } from './markdownParser';
import { patterns } from './regex';

export type CopyAction = 'copy' | 'print';

const stripCitationLinks = (content: string): string =>
  content.replace(patterns.citationLink, (_, text) => text);

interface CopyResearchMessageArgs {
  task: ConversationTask;
  charts: Record<string, HTMLDivElement>;
  tables: Record<string, VisualizationTable>;
  inventory?: ConversationInventory;
  action: CopyAction;
  taskQuestion: string;
}

export const copyResearchMessage = async ({
  task,
  charts,
  tables,
  inventory,
  action,
  taskQuestion,
}: CopyResearchMessageArgs) => {
  if (task.states) {
    await prepareChartFonts();

    let htmlContent = '';
    let plainContent = '';

    for (const curr of task.states) {
      plainContent += `\n\n## ${curr.title}\n\n`;
      htmlContent += htmlNodes.h2(curr.title);

      const processedHtmlContent = await markdownParser({
        content: curr.content,
        charts,
        tables,
      });

      plainContent += stripCitationLinks(curr.content);
      htmlContent += processedHtmlContent + '\n\n';
    }

    const { html: inventoryHtml, text: inventoryText } =
      await buildInventoryContent({
        inventory,
        isCorporateActionInventoryEnabled: useFlags
          .getState()
          .getFeatureFlagState('corporate_action_inventory'),
        isScenarioInventoryEnabled: useFlags
          .getState()
          .getFeatureFlagState('scenario_inventory'),
      });

    plainContent += inventoryText;
    htmlContent += inventoryHtml;

    if (action === 'copy') {
      const { disclaimerHtml } = getDisclaimer();
      htmlContent += disclaimerHtml;
    }

    const startTime = task.last_state?.start_time;

    if (startTime) {
      const formattedTimestamp = formatTimestampWithTz(new Date(startTime));
      const generatedOnLabel = t('chat:generatedOn');

      plainContent += `\n\n${generatedOnLabel} ${formattedTimestamp}`;
      htmlContent += `<p>${generatedOnLabel} ${formattedTimestamp}</p>\n`;
    }

    const copyrightYear = new Date().getFullYear();
    const copyrightText = t('chat:lsegCopyrightDisclaimer', {
      year: copyrightYear,
    });
    const copyrightParagraphs = copyrightText
      .split('\n\n')
      .map(p => `<p>${p}</p>`)
      .join('\n');
    plainContent += `\n\n${copyrightText}`;
    htmlContent += `<div style="margin-top: 24px; font-size: 12px; line-height: 1.4; color: inherit;">\n${copyrightParagraphs}\n</div>\n`;

    const disclaimerText = `${t('chat:disclaimer')}${t('chat:disclaimerContent')}`;
    const openingDisclaimerHtml = `<p style="margin-bottom: 24px; font-size: 12px; line-height: 1.4;">${disclaimerText}</p>\n`;
    const openingDisclaimerPlain = `${disclaimerText}\n\n`;

    if (action === 'print') {
      const brandNamePath = `${config.urls.root}${config.app.basePath}/static/images/brand/logo-light.svg`;
      htmlContent = `<img src="${brandNamePath}" style="width: 103px; height: auto;" />\n${openingDisclaimerHtml}${htmlContent}`;
    } else {
      htmlContent = openingDisclaimerHtml + htmlContent;
    }

    plainContent = openingDisclaimerPlain + plainContent;

    const lightThemeCss = Object.entries(themes.light_default)
      .map(([key, value]) => `${key}: ${value};`)
      .join('\n          ');

    const browserStylesheetStyles = (
      await Promise.all(
        Array.from(document.styleSheets).map(stylesheet =>
          extractStylesheetRules(stylesheet)
        )
      )
    )
      .filter(Boolean)
      .join('\n');

    const printDocument = `
  <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${taskQuestion}</title>
        <style>
          body { ${BODY_STYLE} }

          /* Static CSS files */
          ${resetCss}
          ${variablesCss}
          ${baseCss}
          ${fontsCss}

          /* Stylesheets extracted from the browser's stylesheet API */
          ${browserStylesheetStyles}

          :root {
            ${lightThemeCss}
          }

          @page {
            @bottom-left {
              font-size: 8px;
              content: "Powered by Workspace AI";
            }
            @bottom-right {
              font-size: 8px;
              content: counter(page) "/" counter(pages);
            }
          }
        </style>
      </head>
      <body>
        ${htmlContent}
      </body>
      </html>
  `;

    if (action === 'print') {
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);

      const iframeDoc =
        iframe.contentDocument || iframe.contentWindow?.document;

      if (iframeDoc) {
        const blob = new Blob([printDocument], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        iframe.src = url;

        iframe.onload = () => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();

          setTimeout(() => {
            URL.revokeObjectURL(url);
            document.body.removeChild(iframe);
          }, 100);
        };
      }
    } else {
      const handleCopy = (event: ClipboardEvent) => {
        event.preventDefault();

        if (event.clipboardData) {
          event.clipboardData.clearData();
          event.clipboardData.setData('text/plain', plainContent.trim());
          event.clipboardData.setData('text/html', printDocument);
        }
      };

      document.addEventListener('copy', handleCopy);

      document.execCommand?.('copy');
      document.removeEventListener('copy', handleCopy);
    }
  }
};
